import { useParams } from "react-router";
import { readPlans, type PlanSummary } from "../../builder/api.js";
import { PageHeader, Table, WeekArrows, type Column } from "../../kit/index.js";
import { useModel, usePortal, useTitle } from "../context.js";
import { plansKey, useResource } from "../data.js";
import { dayShort, isDay, mondayOf, monthDay, parseDay } from "../model/days.js";
import { planTitle, plural } from "../model/plan.js";
import { workoutTitle, type WeekModel } from "../model/week.js";
import type { Workout } from "../model/types.js";
import { href } from "../paths.js";
import { Attention } from "./Attention.js";
import { SummaryCard } from "./Summary.js";
import { meetsOn } from "../model/meets.js";
import { DayMeets } from "./MeetMarks.js";
import { useMeets, useTeamPeople } from "./Meets.js";
import { DayPanel, SessionPanel, TrackPanel } from "./Sessions.js";
import { WeekMatrix, type WeekSelection } from "./WeekMatrix.js";
import "./week.css";

/** "Oct 12–18", or "Sep 29–Oct 5" across a month. */
export function weekSpan(model: Pick<WeekModel, "monday" | "sunday">): string {
  const same = parseDay(model.monday).getMonth() === parseDay(model.sunday).getMonth();
  return monthDay(model.monday) + "–" + (same ? String(parseDay(model.sunday).getDate()) : monthDay(model.sunday));
}

/** The published week's own title ("Week 5 · Oct 12–18") when its plan has one. */
export function weekTitle(model: WeekModel, plans: PlanSummary[] | undefined): string {
  const plan = model.published ? plans?.find((p) => p.week === model.monday && p.publishedAt) : undefined;
  const title = plan?.title?.trim();
  return title ? title + " · " + weekSpan(model) : "Week of " + monthDay(model.monday);
}

/** State and totals as one line of text (DESIGN §16.6), never tiles. */
export function weekContext(model: WeekModel): string {
  const parts: string[] = [];
  if (model.monday === mondayOf(model.today)) parts.push("This week");
  if (model.published) {
    const at = String(model.publishedAt ?? "").slice(0, 10);
    parts.push(model.publishedAt && isDay(at) ? "Published " + monthDay(at) : "Published");
  } else parts.push("Not published");
  let done = 0;
  let owed = 0;
  let missed = 0;
  let skipped = 0;
  let prs = 0;
  for (const a of model.athletes) {
    done += a.done;
    owed += a.owed;
    missed += a.missed;
    skipped += a.skipped;
    prs += a.prs;
  }
  if (model.athletes.some((a) => a.total)) {
    parts.push(done + " of " + owed + " done so far");
    if (skipped) parts.push(skipped + " skipped");
    parts.push(missed + " not logged");
  } else parts.push(plural(model.workouts.length, "workout", "workouts"));
  parts.push(plural(prs, "personal best", "personal bests"));
  return parts.join(" · ");
}

function Notes({ model }: { model: WeekModel }) {
  const notes = model.workouts.filter((w) => typeof w.session?.note === "string" && w.session.note.trim());
  if (!notes.length) return null;
  const columns: Column<Workout>[] = [
    { key: "name", label: "Athlete", render: (w) => w.name },
    { key: "session", label: "Session", render: (w) => workoutTitle(model, w) },
    { key: "day", label: "Day", render: (w) => dayShort(w.date) },
    { key: "note", label: "Note", wrap: true, render: (w) => w.session.note!.trim() },
  ];
  return (
    <section className="wk-notes" aria-labelledby="wk-notes-h">
      <h2 id="wk-notes-h">Notes from athletes</h2>
      <Table rows={notes} columns={columns} rowKey={(w) => w.athleteID + (w.session.id ?? w.date)} href={(w) => href(model.monday, "athlete", w.athleteID)} label="Notes from athletes" />
    </section>
  );
}

/** The Week (DESIGN §16.6): the matrix, the notes, and a day or session in a panel over them. */
export function Overview() {
  const m = useModel();
  const { me, weekNav, sheetButton } = usePortal();
  const { date, id, key } = useParams();
  // Only a coach who plans may read plan titles; anyone else reads the week by its dates.
  const plans = useResource(me.person.canPlan ? plansKey : null, () => readPlans().catch((): PlanSummary[] => [])).value;
  const sel: WeekSelection = { date, session: id, track: key };
  const meets = useMeets(m.monday, m.sunday);
  const people = useTeamPeople();
  const dayMeets = date && isDay(date) ? meetsOn(meets ?? [], date) : [];
  const panelTitle = date
    ? isDay(date)
      ? dayShort(date)
      : "Day"
    : id
      ? (() => {
          const cs = m.coach.find((c) => c.id === id);
          return cs?.plan ? planTitle(cs.plan) : "Session";
        })()
      : key
        ? (m.track.find((t) => t.key === key)?.label ?? "Session")
        : null;
  useTitle(panelTitle ? panelTitle + " · Week" : "Week");
  return (
    <div className="k-page">
      <PageHeader title={weekTitle(m, plans)} context={weekContext(m)} lead={<WeekArrows nav={weekNav} />} actions={sheetButton} />
      <SummaryCard week={m.monday} logged={m.workouts.length > 0} />
      <Attention />
      <WeekMatrix sel={sel} />
      <Notes model={m} />
      {date ? (
        <DayPanel date={date} before={dayMeets.length ? <DayMeets meets={dayMeets} athletes={people.athletes} /> : undefined} />
      ) : id ? <SessionPanel id={id} /> : key ? <TrackPanel trackKey={key} /> : null}
    </div>
  );
}
